import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { Redirect } from 'react-router-dom';
import { MARKETING_PLUS_SUITE } from 'self-service-api/constants/BundleTypes';
import ProductInfoContainer from '../components/ProductInfoContainer';
import BannerSection from '../components/banners/BannerSection';
import { IntegrationsSection } from '../v2/containers/IntegrationsSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import ValuePropsSection from '../components/common/ValuePropsSection';
import { CreditsCallout } from '../components/common/credits-callout/CreditsCallout';
import CodeSplitEnvWrapper from '../components/common/env/CodeSplitEnvWrapper';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import SkeletonLoadWrapper from '../components/common/loading/SkeletonLoadWrapper';
import GenericBundleFeatureTable from '../components/feature-table/suite-feature-table/GenericBundleFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import { ProductCardContainer } from '../components/merch-card/card-containers/ProductCardContainer';
import { MARKETING_PLUS_PAGE, MARKETING_PLUS_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { PRICING_AND_PURCHASING_FAILED_STATUS } from '../constants/ErrorCauses';
import { NavItemEnum } from '../constants/routing/NavItems';
import { ENTERPRISE } from '../constants/routing/Tabs';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import ErrorPage from '../pages/ErrorPage';
import { useAppSelector } from '../reducers';
import { isUngatedForMarketingPlusSelector } from '../selectors/GatingSelectors';
import { hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { bundledProductLinesForApiNameSelector } from '../selectors/ProductConfigsSelectors';
import { bundleProductsForBundleTypeSelector } from '../selectors/ProductsSelectors';
import { sortProductLines } from '../utils/sortingUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../v2/components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../v2/components/error-boundary/ErrorSeverity';
import { withErrorBoundary } from '../v2/components/error-boundary/withErrorBoundary';
import PhoneSupportFooter from '../v2/components/phone-support/PhoneSupportFooter';
import { SCREENS } from '../v2/constants/tracking/Screens';
import { MARKETING_PLUS_CARD, MARKETING_PLUS_PAGE_FEATURE_TABLE } from '../v2/constants/tracking/TrackingSources';
import { trackPageView } from '../v2/utils/tracking/trackingEventUtils';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MarketingPlusPage = () => {
  useAnchorLinkScroll();
  useDocumentTitle('marketing-plus');
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const screen = SCREENS.MARKETING_PLUS;
  const navItem = NavItemEnum.MARKETING_PLUS;
  const isUngatedForMarketingPlus = useAppSelector(isUngatedForMarketingPlusSelector);
  useRedirectIfIneligible({
    condition: () => !isUngatedForMarketingPlus
  });
  useEffect(() => {
    if (!isAppReady) return;
    trackPageView({
      screen
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady]);
  const highlightedProducts = useAppSelector(state => {
    if (hasPricingAndPurchasingFailed) return [];
    return bundleProductsForBundleTypeSelector(state, {
      bundleType: MARKETING_PLUS_SUITE
    }).map(({
      apiName
    }) => apiName);
  });
  const productLinesFromBundle = useAppSelector(state => {
    return sortProductLines(bundledProductLinesForApiNameSelector(state, {
      apiName: highlightedProducts[0]
    }));
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  if (hasPricingAndPurchasingFailed) {
    return /*#__PURE__*/_jsx(ErrorPage, {
      errorCause: PRICING_AND_PURCHASING_FAILED_STATUS
    });
  }
  if (!isAppReady) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: navItem
    }),
    errorBoundaryName: MARKETING_PLUS_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    metricsOptions: {
      route: navItem
    },
    children: /*#__PURE__*/_jsxs("div", {
      "data-selenium-id": "page-success",
      children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
        name: "MARKETING_PLUS_PAGE"
      }), /*#__PURE__*/_jsx(HeaderLayout, {
        selectedNavItem: navItem,
        children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
          children: [/*#__PURE__*/_jsx(BannerSection, {
            screen: screen
          }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
            children: [/*#__PURE__*/_jsx(ProductPageTitle, {
              nonProductIdentifier: navItem
            }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
              nonProductIdentifier: navItem
            })]
          }), /*#__PURE__*/_jsx(ProductCardContainer, {
            screen: screen,
            productsToShow: ['marketing-plus-professional-bundle', 'marketing-plus-enterprise-bundle'],
            trackingSource: MARKETING_PLUS_CARD
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(CreditsCallout, {
              navItem: screen
            })
          }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !isAppReady,
            heights: [40],
            widths: ['100%'],
            children: /*#__PURE__*/_jsx(PageSection, {
              children: /*#__PURE__*/_jsx(GenericBundleFeatureTable, {
                apiNames: highlightedProducts,
                productLinesFromBundle: productLinesFromBundle,
                screen: screen,
                trackingSource: MARKETING_PLUS_PAGE_FEATURE_TABLE
              }, screen)
            })
          }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
            publicComponentProps: {
              tab: ENTERPRISE,
              screen
            }
          }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsx(PhoneSupportFooter, {
            phoneSourceId: "marketingplus-footer",
            screen: screen,
            apiName: "marketing-plus-professional-bundle"
          })]
        })
      })]
    })
  });
};
export default withErrorBoundary(MarketingPlusPage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: MARKETING_PLUS_PAGE
});